import axios from 'axios';

const LOCAL_SERVER_URL = 'http://localhost:5000';
const PROD_SERVER_URL = 'https://project-control-3.onrender.com';
const isLocalHost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';
const DEFAULT_SERVER_URL = isLocalHost ? LOCAL_SERVER_URL : PROD_SERVER_URL;
const API_URL = process.env.REACT_APP_API_URL || `${DEFAULT_SERVER_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

// Attach JWT token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle auth errors globally
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (err.message === 'Network Error' || !err.response) {
      console.error('🌐 Backend Connection Error:', err.message);
      console.error(`Make sure backend is reachable on ${API_URL}`);
    }
    return Promise.reject(err);
  }
);

// ── Auth ───────────────────────────────────────────────
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  children: () => api.get('/auth/children'),
  getPairCode: () => api.get('/devices/pair-code'),
  updatePermissions: (permissions) => api.put('/auth/permissions', { permissions }),
};

// ── Location ───────────────────────────────────────────
export const locationAPI = {
  getCurrent: (childId) => api.get(`/location/current/${childId}`),
  getHistory: (childId, hours = 24) => api.get(`/location/history/${childId}?hours=${hours}`),
  update: (data) => api.post('/location/update', data),
};

// ── Analytics ──────────────────────────────────────────
export const analyticsAPI = {
  getUsage: (childId, days = 7) => api.get(`/analytics/usage/${childId}?days=${days}`),
  getSummary: (childId) => api.get(`/analytics/summary/${childId}`),
  postUsage: (data) => api.post('/analytics/usage', data),
};

// ── Control ────────────────────────────────────────────
export const controlAPI = {
  lockDevice: (childId) => api.post('/control/lock', { childId }),
  triggerAlarm: (childId, message) => api.post('/control/alarm', { childId, message }),
  requestCamera: (childId, facing = 'front') => api.post('/control/request-camera', { childId, facing }),
  requestMic: (childId) => api.post('/control/request-microphone', { childId }),
  requestScreen: (childId) => api.post('/control/request-screen', { childId }),
  getDeviceStatus: (childId) => api.get(`/control/device-status/${childId}`),
};

// ── SOS ────────────────────────────────────────────────
export const sosAPI = {
  getAlerts: () => api.get('/sos/alerts'),
  acknowledge: (alertId) => api.put(`/sos/acknowledge/${alertId}`),
  getAudio: (alertId) => api.get(`/sos/audio/${alertId}`),
  trigger: (data) => api.post('/sos/trigger', data),
};

// ── Logs ───────────────────────────────────────────────
export const logsAPI = {
  getLogs: (params) => api.get('/logs', { params }),
  getRecent: () => api.get('/logs/recent'),
};

// ── Devices ────────────────────────────────────────────
export const devicesAPI = {
  getAll: () => api.get('/devices'),
  unlink: (childId) => api.delete(`/devices/${childId}`),
  getPairCode: () => api.get('/devices/pair-code'),
};

// ── Chat ───────────────────────────────────────────────
export const chatAPI = {
  getParentHistory: (childId, limit = 100) => api.get(`/chat/history/${childId}?limit=${limit}`),
  getChildHistory: (limit = 100) => api.get(`/chat/history?limit=${limit}`),
};

// Expose API URL for debugging
export const getAPIURL = () => API_URL;

export default api;
